import { useMutation, useQueryClient } from "@tanstack/react-query";

import {
  ActivityDto,
  CommentRequestDto,
  ReminderDto,
  TicketDto,
} from "../../api-client";
import { useApi } from "../../api/ApiProvider";
import { ticketKeys } from "./ticket.keys";
import { transactionKeys } from "../Transactions/transaction.keys";

/**
 * NOTE: Global error/success handling is configured in src/stores/queryClient.ts
 * - MutationCache handles automatic error notifications via notificationStore
 * - MutationCache handles automatic success notifications via meta.successMessage
 * - No need to manually call notificationStore in individual mutations
 */

// =============================================================================
// Helper: Invalidate All Ticket Caches
// =============================================================================

/**
 * Invalidates all ticket-related caches to ensure dashboard widgets and lists refresh.
 * Call this after any mutation that affects ticket data.
 */
const invalidateAllTicketCaches = async (
  queryClient: ReturnType<typeof useQueryClient>,
  ticketId?: number
) => {
  const invalidations = [
    // Main ticket lists
    queryClient.invalidateQueries({ queryKey: ticketKeys.tickets.all() }),
    
   
    // Dashboard digest queries - these power TeamDigest, PersonalDigest, RxOrdersDigest
    queryClient.invalidateQueries({ queryKey: ticketKeys.expiredTickets.all() }),
    queryClient.invalidateQueries({ queryKey: ticketKeys.ticketsByDate.all() }),
    
    // Activities (comments, reminders)
    queryClient.invalidateQueries({ queryKey: ticketKeys.activities.all() }),

    // Transaction-related tickets
    queryClient.invalidateQueries({ queryKey: transactionKeys.transactionTickets.all()}),
  ];

  // If a specific ticket ID is provided, also invalidate that ticket's cache
  if (ticketId) {
    invalidations.push(
      queryClient.invalidateQueries({ queryKey: ticketKeys.ticketById.all(ticketId) })
    );
  }

  await Promise.all(invalidations);
};

// =============================================================================
// Create Ticket Mutation
// =============================================================================

/**
 * Mutation to create a new ticket
 */
export const useCreateTicket = () => {
  const queryClient = useQueryClient();
  const { tickets: ticketsApi } = useApi();

  return useMutation<TicketDto, Error, TicketDto>({
    mutationFn: (ticketData: TicketDto) => {
      return ticketsApi.createTicket(ticketData);
    },
    onSuccess: async (savedTicket) => {
      // Set the new ticket in cache immediately
      if (savedTicket.id) {
        queryClient.setQueryData(ticketKeys.ticketById.all(savedTicket.id), savedTicket);
      }

      // Invalidate all ticket caches to refresh lists and digests
      await invalidateAllTicketCaches(queryClient, savedTicket.id);
    },
    meta: {
      successMessage: "Ticket erfolgreich erstellt",
      errorMessage: "Fehler beim Erstellen des Tickets",
    },
  });
};

// =============================================================================
// Update Ticket Mutation
// =============================================================================

interface UpdateTicketVariables {
  ticketId: number;
  ticketData: TicketDto;
}

/**
 * Mutation to update an existing ticket
 */
export const useUpdateTicket = () => {
  const queryClient = useQueryClient();
  const { tickets: ticketsApi } = useApi();

  return useMutation<TicketDto, Error, UpdateTicketVariables>({
    mutationFn: ({ ticketId, ticketData }) => {
      return ticketsApi.updateTickets(ticketId, ticketData);
    },
    onSuccess: async (savedTicket, { ticketId }) => {
      // Update the specific ticket in cache if it exists
      const existingTicket = queryClient.getQueryData(ticketKeys.ticketById.all(ticketId));
      if (existingTicket) {
        queryClient.setQueryData(ticketKeys.ticketById.all(ticketId), savedTicket);
      }

      // Invalidate all ticket caches to refresh lists and digests
      await invalidateAllTicketCaches(queryClient, ticketId);
    },
    meta: {
      successMessage: "Ticket erfolgreich aktualisiert",
      errorMessage: "Fehler beim Aktualisieren des Tickets",
    },
  });
};

// =============================================================================
// Comment Mutations
// =============================================================================

interface AddCommentVariables {
  ticketId: number;
  commentData: CommentRequestDto;
}

/**
 * Mutation to add or update a comment on a ticket
 */
export const useAddComment = () => {
  const queryClient = useQueryClient();
  const { tickets: ticketsApi } = useApi();

  return useMutation<ActivityDto, Error, AddCommentVariables>({
    mutationFn: async ({ ticketId, commentData }) => {
      return await ticketsApi.createOrUpdateComment(ticketId, commentData);
    },
    onSuccess: async (_, { ticketId }) => {
      // Invalidate all ticket caches - comments affect last activity display in digests
      await invalidateAllTicketCaches(queryClient, ticketId);
    },
    meta: {
      successMessage: "Kommentar erfolgreich gespeichert",
      errorMessage: "Fehler beim Speichern des Kommentars",
    },
  });
};

interface DeleteCommentVariables {
  ticketId?: number;
  activityId: string;
}

/**
 * Mutation to delete a comment from a ticket
 */
export const useDeleteComment = () => {
  const queryClient = useQueryClient();
  const { tickets: ticketsApi } = useApi();

  return useMutation<void, Error, DeleteCommentVariables>({
    mutationFn: ({ activityId }) => ticketsApi.deleteComment(activityId),
    onSuccess: async (_, { ticketId }) => {
      // Invalidate all ticket caches
      await invalidateAllTicketCaches(queryClient, ticketId);
    },
    meta: {
      successMessage: "Kommentar erfolgreich gelöscht",
      errorMessage: "Fehler beim Löschen des Kommentars",
    },
  });
};

// =============================================================================
// Reminder Mutations
// =============================================================================

interface CreateReminderInput {
  ticketId: number;
  date: Date;
  alternativeMessage?: string;
}

/**
 * Mutation to create a reminder for a ticket
 */
export const useCreateReminder = () => {
  const queryClient = useQueryClient();
  const { tickets: ticketsApi } = useApi();

  return useMutation<ReminderDto, Error, CreateReminderInput>({
    mutationFn: async ({ ticketId, ...reminderData }) => {
      const response = await ticketsApi.createReminder(ticketId, reminderData);
      return response;
    },
    onSuccess: async (_, { ticketId }) => {
      // Invalidate all ticket caches - reminders affect expired tickets and date-based queries
      await invalidateAllTicketCaches(queryClient, ticketId);
    },
    meta: {
      successMessage: "Erinnerung erfolgreich erstellt",
      errorMessage: "Fehler beim Erstellen der Erinnerung",
    },
  });
};

interface UpdateReminderInput {
  ticketId: number;
  reminderId: number;
  date?: string;
  alternativeMessage?: string;
  isResolved?: boolean;
}

/**
 * Mutation to update a reminder on a ticket
 */
export const useUpdateReminder = () => {
  const queryClient = useQueryClient();
  const { tickets: ticketsApi } = useApi();

  return useMutation<void, Error, UpdateReminderInput>({
    mutationFn: ({ reminderId, date, alternativeMessage, isResolved }) =>
      ticketsApi.editReminder(reminderId, {
        date: date ?? "",
        alternativeMessage: alternativeMessage ?? "",
        isResolved: isResolved ?? false,
      }),
    onSuccess: async (_, { ticketId }) => {
      // Invalidate all ticket caches - reminder changes affect expired tickets and date queries
      await invalidateAllTicketCaches(queryClient, ticketId);
    },
    meta: {
      successMessage: "Erinnerung erfolgreich aktualisiert",
      errorMessage: "Fehler beim Aktualisieren der Erinnerung",
    },
  });
};

interface DeleteReminderInput {
  ticketId: number;
  reminderId: number;
}

/**
 * Mutation to delete a reminder from a ticket
 */
export const useDeleteReminder = () => {
  const queryClient = useQueryClient();
  const { tickets: ticketsApi } = useApi();

  return useMutation<void, Error, DeleteReminderInput>({
    mutationFn: ({ reminderId }) => ticketsApi.deleteReminder(reminderId),
    onSuccess: async (_, { ticketId }) => {
      // Invalidate all ticket caches
      await invalidateAllTicketCaches(queryClient, ticketId);
    },
    meta: {
      successMessage: "Erinnerung erfolgreich gelöscht",
      errorMessage: "Fehler beim Löschen der Erinnerung",
    },
  });
};
